import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Save } from 'lucide-react';
import { AppContext } from '../../../context/AppContext';
import { PostCallWithErrorResponse, simpleGetCallWithErrorResponse } from '../../../api/ApiServices';
import { Button, Card, Input, Select, Textarea, MultiSelect } from '../../../components/ui';
import { ToastMsg } from '../../TostMsg';
import styles from './PushCampaigns.module.scss';

const ROLE = 'CRM Admin';
const PLATFORM = 'Push';
const USER_TYPE = 'Customer';
const SEARCH_DEBOUNCE_MS = 300;

const NAME_MIN = 3;
const NAME_MAX = 80;
const DESC_MAX = 250;
const VALID_GROUP_TYPES = ['Custom', 'System', 'Dynamic'];

const initialForm = {
  groupName: '',
  groupDescription: '',
  groupType: 'Custom',
  groupMembers: [],
};

const validateField = (name, value, form) => {
  switch (name) {
    case 'groupName': {
      const v = (value ?? '').trim();
      if (!v) return 'Group name is required.';
      if (v.length < NAME_MIN) return `Group name must be at least ${NAME_MIN} characters.`;
      if (v.length > NAME_MAX) return `Group name cannot exceed ${NAME_MAX} characters.`;
      return '';
    }
    case 'groupDescription': {
      const v = value ?? '';
      if (v.length > DESC_MAX) return `Description cannot exceed ${DESC_MAX} characters.`;
      return '';
    }
    case 'groupType': {
      if (!value) return 'Group type is required.';
      if (!VALID_GROUP_TYPES.includes(value)) return 'Select a valid group type.';
      return '';
    }
    case 'groupMembers': {
      const arr = Array.isArray(value) ? value : form?.groupMembers ?? [];
      if (arr.length === 0) return 'Select at least one member.';
      return '';
    }
    default:
      return '';
  }
};

const toUserOption = (u) => {
  const id = u.id ?? u.user_id ?? u.userId;
  const name =
    u.name ||
    u.full_name ||
    u.fullName ||
    [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
    u.email ||
    u.phone ||
    `User #${id}`;
  const sublabel = u.email || u.phone || u.mobile || '';
  return { value: id, label: name, sublabel };
};

export default function PushGroupCreate() {
  const { sidebar, ApiConfigg } = useContext(AppContext);
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUsersCache, setSelectedUsersCache] = useState({});

  const debounceRef = useRef(null);

  const clearError = (name) => {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const err = validateField(name, value, form);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const fetchUsers = (q) => {
    const url = ApiConfigg?.GET_USER_LIST;
    if (!url) return;
    const qs = [
      `search=${encodeURIComponent(q || '')}`,
      `type=${encodeURIComponent(USER_TYPE)}`,
      `role=${ROLE}`,
    ].join('&');
    setUsersLoading(true);
    simpleGetCallWithErrorResponse(`${url}?${qs}`)
      .then((res) => {
        const json = res?.json;
        const list = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.data?.users)
          ? json.data.users
          : Array.isArray(json) ? json : [];
        setUsers(list.map(toUserOption).filter((o) => o.value != null));
      })
      .catch((err) => console.error('User list fetch error:', err))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    fetchUsers('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ApiConfigg?.GET_USER_LIST]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchUsers(userSearch), SEARCH_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch]);

  const memberOptions = useMemo(() => {
    const seen = new Set(users.map((u) => String(u.value)));
    const cached = Object.values(selectedUsersCache).filter(
      (u) => u && !seen.has(String(u.value))
    );
    return [...users, ...cached];
  }, [users, selectedUsersCache]);

  const handleMembersChange = (next) => {
    setSelectedUsersCache((prev) => {
      const updated = { ...prev };
      next.forEach((id) => {
        if (!updated[id]) {
          const found = users.find((u) => String(u.value) === String(id));
          if (found) updated[id] = found;
        }
      });
      return updated;
    });
    setForm((prev) => ({ ...prev, groupMembers: next }));
    if (next.length > 0) clearError('groupMembers');
  };

  const validateAll = () => {
    const fields = ['groupName', 'groupDescription', 'groupType', 'groupMembers'];
    const next = {};
    fields.forEach((name) => {
      const err = validateField(name, form[name], form);
      if (err) next[name] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const url = ApiConfigg?.POST_PROMOTION_GROUP;
    if (!url) {
      ToastMsg('error', 'Push groups endpoint is not configured.');
      return;
    }
    if (!validateAll()) {
      ToastMsg('error', 'Please fix the highlighted fields.');
      return;
    }

    const payload = {
      groupName: form.groupName.trim(),
      groupDescription: form.groupDescription.trim(),
      groupType: form.groupType,
      platform: PLATFORM,
      role: ROLE,
      groupMembers: form.groupMembers.map((id) =>
        Number.isFinite(Number(id)) ? Number(id) : id
      ),
    };

    setSubmitting(true);
    PostCallWithErrorResponse(url, JSON.stringify(payload))
      .then((res) => {
        if (!res?.error) {
          ToastMsg('success', res?.json?.message || 'Group created successfully.');
          navigate('/PushGroups');
        } else {
          ToastMsg('error', res?.json?.message || 'Failed to create group.');
        }
      })
      .catch((err) => {
        console.error('Create push group error:', err);
        ToastMsg('error', 'Network error. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  const nameCount = form.groupName.length;
  const descCount = form.groupDescription.length;

  return (
    <div className={sidebar ? 'taskMain' : 'cx-active taskMain'} id="cx-main">
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button
              variant="ghost"
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/PushGroups')}
            >
              Back
            </Button>
            <span className={styles.iconBadge}>
              <Users size={20} />
            </span>
            <div className={styles.titleBlock}>
              <h1>Create Push Group</h1>
              <p>Define a target audience group for push notification campaigns.</p>
            </div>
          </div>
        </div>

        <Card padding="lg" elevation="sm">
          <form onSubmit={handleSubmit} noValidate>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Group details</div>
              <div className={styles.formGrid}>
                <div className="full">
                  <Input
                    id="groupName"
                    name="groupName"
                    label="Group name"
                    placeholder="e.g. Push QA Test Group"
                    value={form.groupName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={NAME_MAX}
                    error={errors.groupName || ''}
                    helperText={!errors.groupName ? `${nameCount}/${NAME_MAX}` : ''}
                  />
                </div>
                <div className="full">
                  <Textarea
                    id="groupDescription"
                    name="groupDescription"
                    label="Description"
                    placeholder="Manual QA — push notification campaign"
                    rows={3}
                    value={form.groupDescription}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={DESC_MAX}
                    error={errors.groupDescription || ''}
                    helperText={!errors.groupDescription ? `${descCount}/${DESC_MAX}` : ''}
                  />
                </div>
                <div>
                  <Select
                    id="groupType"
                    name="groupType"
                    label="Group type"
                    value={form.groupType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.groupType || ''}
                  >
                    {VALID_GROUP_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Input
                    label="Platform"
                    value={PLATFORM}
                    readOnly
                    disabled
                    helperText="Push groups always target the Push platform."
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Members</div>
              <MultiSelect
                id="groupMembers"
                label=""
                placeholder="Search and select members"
                value={form.groupMembers}
                onChange={handleMembersChange}
                options={memberOptions}
                searchValue={userSearch}
                onSearchChange={setUserSearch}
                loading={usersLoading}
                emptyMessage={
                  userSearch
                    ? `No users match "${userSearch}"`
                    : 'No users available'
                }
                loadingMessage="Loading users…"
                error={errors.groupMembers || ''}
                helperText={
                  !errors.groupMembers
                    ? 'Search by name, email, or phone. Pick one or more users to include.'
                    : ''
                }
              />
            </div>

            <div className={styles.formActions}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/PushGroups')}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                rightIcon={<Save size={16} />}
              >
                Create Group
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
